<template>
  <div class="form-group">
    <label v-if="label">
      {{ label }}
      <span v-if="optional" class="optional">(optional)</span>
    </label>
    <!-- The control itself (an <input>, <select>, …) is projected here. -->
    <slot />
    <small v-if="error" class="cl-err">{{ error }}</small>
  </div>
</template>

<script setup lang="ts">
/**
 * Shared label + error chrome for a form field.
 *
 * This is the single source of the field wrapper used across the admin forms:
 * a <label> (with an optional "(optional)" tag), a slot for whatever control
 * the field renders, and an inline <small class="cl-err"> error line.
 *
 * It owns NO validation state — it is pure presentation. The parent (or an
 * input wrapper like PhoneInput/EmailInput) decides WHEN to pass an `error`
 * string; FormField only decides HOW the label/error look.
 */
withDefaults(
  defineProps<{
    label?: string
    optional?: boolean
    /** When set to a non-empty string, the inline error line is shown. */
    error?: string
  }>(),
  {
    label: '',
    optional: false,
    error: undefined,
  },
)
</script>

<style scoped>
/* cl-err is defined globally (ClientsView) but scope a fallback so the error is
   always visible even where that global style is not in play. */
.cl-err {
  display: block;
  margin-top: 0.25rem;
  color: var(--danger-text, var(--danger, #e5484d));
  font-size: 0.78rem;
}
</style>
